import {z} from "zod";

export const WORKFLOW_IMAGE_AUDIO_SLIDESHOW = "image-audio-slideshow-v1" as const;
export const WORKFLOW_IMAGE_MEDIA_SLIDESHOW = "image-media-slideshow-v2" as const;

export const WorkflowSchema = z.enum([
  WORKFLOW_IMAGE_AUDIO_SLIDESHOW,
  WORKFLOW_IMAGE_MEDIA_SLIDESHOW
]);

export const TaskStatusSchema = z.enum([
  "draft",
  "uploading",
  "queued",
  "claimed",
  "processing",
  "uploading_result",
  "completed",
  "failed",
  "cancelled"
]);

export const AssetKindSchema = z.enum(["image", "audio", "video", "captions", "result"]);
export const AssetStatusSchema = z.enum(["pending", "uploaded", "verified"]);

const UploadFileSchema = z.object({
  name: z.string().trim().min(1).max(240),
  mimeType: z.string().trim().min(1).max(120),
  sizeBytes: z.number().int().positive().max(5 * 1024 * 1024 * 1024)
});

export const SlideshowSettingsSchema = z.object({
  width: z.literal(1080).default(1080),
  height: z.literal(1440).default(1440),
  fps: z.literal(30).default(30),
  title: z.string().trim().max(120).optional(),
  subtitle: z.string().trim().max(120).optional(),
  transitionMs: z.number().int().min(150).max(1500).default(500),
  minimumSilenceMs: z.number().int().min(100).max(2000).default(350),
  minimumCaptionGapMs: z.number().int().min(0).max(1000).default(120),
  silenceToleranceMs: z.number().int().min(100).max(3000).default(650),
  maxImageHoldMs: z.literal(10000).default(10000),
  recentImageExclusionCount: z.number().int().min(0).max(3).default(3)
}).default({
  width: 1080,
  height: 1440,
  fps: 30,
  transitionMs: 500,
  minimumSilenceMs: 350,
  minimumCaptionGapMs: 120,
  silenceToleranceMs: 650,
  maxImageHoldMs: 10000,
  recentImageExclusionCount: 3
});

export const CreateDraftRequestSchema = z.object({
  workflow: z.literal(WORKFLOW_IMAGE_MEDIA_SLIDESHOW).default(WORKFLOW_IMAGE_MEDIA_SLIDESHOW),
  title: z.string().trim().max(120).optional(),
  source: UploadFileSchema,
  images: z.array(UploadFileSchema).min(1).max(60),
  settings: SlideshowSettingsSchema
});

export type CreateDraftRequest = z.infer<typeof CreateDraftRequestSchema>;
export type SlideshowSettings = z.infer<typeof SlideshowSettingsSchema>;
export type TaskStatus = z.infer<typeof TaskStatusSchema>;
export type AssetKind = z.infer<typeof AssetKindSchema>;

export type UploadTarget = {
  assetId: string;
  kind: AssetKind;
  sortIndex: number;
  url: string;
  method: "PUT";
  expiresAt: string;
};

export const TaskSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string().nullable(),
  workflow: WorkflowSchema,
  status: TaskStatusSchema,
  progress: z.number().int().min(0).max(100),
  phase: z.string().nullable(),
  imageCount: z.number().int().min(1).max(60),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  trashedAt: z.string().nullable()
});

export const TaskAssetSchema = z.object({
  id: z.string().uuid(),
  kind: AssetKindSchema,
  sortIndex: z.number().int().min(0),
  originalName: z.string(),
  objectKey: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().nonnegative(),
  status: AssetStatusSchema
});

export const TaskEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  phase: z.string().nullable(),
  message: z.string(),
  createdAt: z.string()
});

export const TaskDetailSchema = TaskSummarySchema.extend({
  settings: SlideshowSettingsSchema,
  assets: z.array(TaskAssetSchema),
  events: z.array(TaskEventSchema),
  resultUrl: z.string().url().nullable()
});

export type TaskSummary = z.infer<typeof TaskSummarySchema>;
export type TaskAsset = z.infer<typeof TaskAssetSchema>;
export type TaskEvent = z.infer<typeof TaskEventSchema>;
export type TaskDetail = z.infer<typeof TaskDetailSchema>;

export const TaskBatchRequestSchema = z.object({
  taskIds: z.array(z.string().uuid()).min(1).max(100)
}).superRefine(({taskIds}, context) => {
  if (new Set(taskIds).size !== taskIds.length) {
    context.addIssue({code: "custom", message: "任务编号不能重复", path: ["taskIds"]});
  }
});

export type TaskBatchRequest = z.infer<typeof TaskBatchRequestSchema>;

export const LocalCleanupJobSchema = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  attemptIds: z.array(z.string().uuid()),
  leaseToken: z.string().uuid()
});

export type LocalCleanupJob = z.infer<typeof LocalCleanupJobSchema>;

export const UploadTargetSchema = z.object({
  assetId: z.string().uuid(),
  kind: AssetKindSchema,
  sortIndex: z.number().int().min(0),
  url: z.string().url(),
  method: z.literal("PUT"),
  expiresAt: z.string()
});

export const WorkerClaimSchema = z.object({
  task: TaskDetailSchema,
  attemptId: z.string().uuid(),
  leaseToken: z.string().uuid(),
  leaseExpiresAt: z.string(),
  inputs: z.array(TaskAssetSchema.extend({downloadUrl: z.string().url()})),
  resultUpload: UploadTargetSchema
});

export type WorkerClaim = z.infer<typeof WorkerClaimSchema>;

export const WorkerProgressSchema = z.object({
  leaseToken: z.string().uuid(),
  progress: z.number().int().min(0).max(99),
  phase: z.string().trim().min(1).max(80),
  message: z.string().trim().max(500).optional()
});

export const WorkerCompleteSchema = z.object({
  leaseToken: z.string().uuid(),
  resultAssetId: z.string().uuid(),
  report: z.record(z.string(), z.unknown()).optional()
});

export const WorkerFailSchema = z.object({
  leaseToken: z.string().uuid(),
  code: z.string().trim().min(1).max(80),
  message: z.string().trim().min(1).max(500),
  retryable: z.boolean().default(false)
});
